import {DateObject} from 'react-multi-date-picker'
import gregorian from 'react-date-object/calendars/gregorian'
import persian from 'react-date-object/calendars/persian'
import gregorian_en from 'react-date-object/locales/gregorian_en'
import persian_fa from 'react-date-object/locales/persian_fa'

/** A Jalali day, with the time the value carries (00:00 when it has none). */
export type PersianDateParts = {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  hasTime: boolean
}

export const PERSIAN_MONTHS = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
]

/** Saturday first, as `DateObject.weekDay.index` counts in the Persian calendar. */
export const PERSIAN_WEEKDAYS = [
  'شنبه',
  'یکشنبه',
  'دوشنبه',
  'سه‌شنبه',
  'چهارشنبه',
  'پنجشنبه',
  'جمعه',
]

export const FRIDAY_INDEX = 6

export const toLatinDigits = (value: string) =>
  value
    .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))

export const toPersianDigits = (value: string | number) =>
  String(value).replace(/\d/g, (digit) => '۰۱۲۳۴۵۶۷۸۹'[Number(digit)])

export const persianMonthLength = (year: number, month: number) =>
  persian.getMonthLengths(persian.isLeap(year))[month - 1]

const pad = (value: number) => String(value).padStart(2, '0')

/**
 * Reads the values the panel keeps for a date field — `YYYY/MM/DD`, optionally followed by
 * `HH:mm` (seconds are tolerated) — in Latin, Persian or Arabic digits. Null when it is not a
 * real Jalali day.
 */
export const parsePersianDateValue = (value?: unknown): PersianDateParts | null => {
  if (value === null || value === undefined) return null
  const text = toLatinDigits(String(value)).trim().replace(/[-.]/g, '/').replace(/\s+/g, ' ')
  const match =
    /^(\d{4})\/(\d{1,2})\/(\d{1,2})(?:[ T](\d{1,2}):(\d{1,2})(?::\d{1,2}(?:\.\d+)?)?)?$/.exec(text)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (month < 1 || month > 12 || day < 1 || day > persianMonthLength(year, month)) return null
  const hasTime = match[4] !== undefined
  const hour = hasTime ? Number(match[4]) : 0
  const minute = hasTime ? Number(match[5]) : 0
  if (hour > 23 || minute > 59) return null
  return {year, month, day, hour, minute, hasTime}
}

/**
 * What a person typed into a date field: the forms above, or the digits run together
 * (`14050701`, or `140507011430` with the time), as a keypad without «/» and «:» types them.
 */
export const parseTypedPersianDate = (value: string): PersianDateParts | null => {
  const text = toLatinDigits(value).trim()
  const compact = /^(\d{4})(\d{2})(\d{2})(?:(\d{2})(\d{2}))?$/.exec(text)
  if (!compact) return parsePersianDateValue(text)
  const [, year, month, day, hour, minute] = compact
  return parsePersianDateValue(`${year}/${month}/${day}${hour ? ` ${hour}:${minute}` : ''}`)
}

/**
 * Typing a day on a card reader's numeric keypad, which has no «/», «:» or space: once the year,
 * a two-digit month, the day or the hour is complete, the separator that follows is added, and a
 * separator typed right after one is dropped. Anything but one character added at the end is
 * left as it is.
 */
export const continueTypedPersianDate = (previous: string, next: string, withTime: boolean) => {
  if (next.length !== previous.length + 1 || !next.startsWith(previous)) return next
  if (/[/.\-: ]$/.test(next) && /[/: ]$/.test(previous)) return previous
  const text = toLatinDigits(next)
  if (/^\d{4}$/.test(text) || /^\d{4}[/.-]\d{2}$/.test(text)) return `${next}/`
  if (withTime && /^\d{4}[/.-]\d{1,2}[/.-]\d{2}$/.test(text)) return `${next} `
  if (withTime && /^\d{4}[/.-]\d{1,2}[/.-]\d{1,2} \d{2}$/.test(text)) return `${next}:`
  return next
}

/** The value the panel stores: `YYYY/MM/DD`, or `YYYY/MM/DD HH:mm`, always in Latin digits. */
export const formatPersianDateValue = (
  parts: Pick<PersianDateParts, 'year' | 'month' | 'day'> & {hour?: number; minute?: number},
  withTime: boolean
) =>
  `${parts.year}/${pad(parts.month)}/${pad(parts.day)}` +
  (withTime ? ` ${pad(parts.hour ?? 0)}:${pad(parts.minute ?? 0)}` : '')

/** A number that orders Jalali days. */
export const persianDayKey = (parts: Pick<PersianDateParts, 'year' | 'month' | 'day'>) =>
  parts.year * 10000 + parts.month * 100 + parts.day

export const toPersianDateObject = (
  parts: Pick<PersianDateParts, 'year' | 'month' | 'day'> & {hour?: number; minute?: number}
) =>
  new DateObject({
    calendar: persian,
    locale: persian_fa,
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour ?? 0,
    minute: parts.minute ?? 0,
    second: 0,
    millisecond: 0,
  })

export const toGregorian = (date: DateObject) =>
  new DateObject(date).convert(gregorian, gregorian_en)

/** The current Jalali day and time in Tehran, whatever the browser's time zone. */
export const tehranNow = (now: Date | number = new Date()): PersianDateParts => {
  const parts = new Intl.DateTimeFormat('en-US-u-ca-persian', {
    timeZone: 'Asia/Tehran',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  })
    .formatToParts(new Date(now))
    .reduce<Record<string, number>>((values, part) => {
      if (['year', 'month', 'day', 'hour', 'minute'].includes(part.type)) {
        values[part.type] = Number(toLatinDigits(part.value))
      }
      return values
    }, {})
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour % 24,
    minute: parts.minute,
    hasTime: true,
  }
}

/** «دوشنبه ۶ مهر ۱۴۰۵» */
export const describePersianDay = (parts: Pick<PersianDateParts, 'year' | 'month' | 'day'>) => {
  const date = toPersianDateObject(parts)
  return `${PERSIAN_WEEKDAYS[date.weekDay.index]} ${toPersianDigits(parts.day)} ${
    PERSIAN_MONTHS[parts.month - 1]
  } ${toPersianDigits(parts.year)}`
}

/** «Mon, 28 Sep 2026» */
export const describeGregorianDay = (parts: Pick<PersianDateParts, 'year' | 'month' | 'day'>) =>
  toGregorian(toPersianDateObject(parts)).format('ddd, D MMM YYYY')

/** The Gregorian months a Jalali month spans: «Sep – Oct 2026», «Dec 2026 – Jan 2027». */
export const gregorianSpanOfPersianMonth = (year: number, month: number) => {
  const first = toGregorian(toPersianDateObject({year, month, day: 1}))
  const last = toGregorian(toPersianDateObject({year, month, day: persianMonthLength(year, month)}))
  const firstMonth = first.month.shortName
  const lastMonth = last.month.shortName
  if (first.year !== last.year) return `${firstMonth} ${first.year} – ${lastMonth} ${last.year}`
  if (firstMonth === lastMonth) return `${firstMonth} ${first.year}`
  return `${firstMonth} – ${lastMonth} ${first.year}`
}

/** The Gregorian years a Jalali year spans: «2026 – 2027». */
export const gregorianSpanOfPersianYear = (year: number) => {
  const first = toGregorian(toPersianDateObject({year, month: 1, day: 1})).year
  const last = toGregorian(
    toPersianDateObject({year, month: 12, day: persianMonthLength(year, 12)})
  ).year
  return first === last ? String(first) : `${first} – ${last}`
}
